import { useState, useCallback, useMemo, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Building2,
  MoreHorizontal,
  Users,
  CheckCircle,
  XCircle,
  Plus,
  Loader2,
  CreditCard,
  Pencil,
  Trash2,
  RefreshCw,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import { TimezoneCombobox } from "@/components/ui/timezone-combobox";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pagination } from "@/components/ui/pagination";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { canRunClientMaintenanceSweep } from "@/lib/clientMaintenanceSweepAccess";
import { ClientMaintenanceSweepDrawer } from "@/components/portal/platform/ClientMaintenanceSweepDrawer";
import { platformApi, Organisation } from "@/services/api/platform";
import { usePaginatedData, PaginatedResponse } from "@/hooks/usePaginatedData";
import { useCountries } from "@/hooks/useCountries";
import { resolveVatTreatment } from "@/lib/countries";
import type { VatTreatment } from "@/lib/countries";
import type { CountryItem } from "@/services/api/platform";
import { fetchPrimaryTimezone } from "@/lib/timezones";

export const Route = createFileRoute("/portal/platform/organisations")({
  component: PlatformOrganisationsPage,
});

/**
 * How a country's VAT treatment reads under the currency line.
 *
 * The old wording tied VAT to CURRENCY ("ZA ⇒ incl. 15% VAT", everything else
 * "ex VAT") and was wrong twice over: currency does not decide VAT, and a
 * foreign sale is a zero-rated export — VAT201-reportable at 0% — not merely
 * "ex VAT". The rate is read off the country row rather than hardcoded.
 */
function vatTreatmentHint(
  treatment: VatTreatment | null,
  rate: number,
): string {
  switch (treatment) {
    case "STANDARD":
      return ` (incl. ${(rate * 100).toFixed(0)}% VAT)`;
    case "ZERO_RATED_EXPORT":
      return " (zero-rated export)";
    case "EXEMPT":
      return " (VAT exempt)";
    default:
      return "";
  }
}

interface FormData {
  name: string;
  slug: string;
  time_zone: string;
  country_code: string;
  /**
   * The org's CURRENT currency, echoed straight back on save. It is displayed
   * read-only and never derived from the country: changing an organisation's
   * billing currency is a deliberate, separate admin action.
   */
  billing_currency: string;
}

function PlatformOrganisationsPage() {
  const { token } = useAuth();
  const { refreshOrganisations } = usePortalData();
  const { isPlatformAdmin, can } = usePermissions();
  // The sweep runs OWNER-gated maintenance against an arbitrary client, which
  // only a Larasoft platform admin resolves to. Mirrors the BE (parity test in
  // clientMaintenanceSweepAccess.parity.test.ts).
  const canSweep = canRunClientMaintenanceSweep({
    isPlatformAdmin,
    canExecute: can("execute", "tables"),
  });
  const [sweepOrg, setSweepOrg] = useState<Organisation | null>(null);

  /**
   * The sweep pins every request to the chosen client with an explicit
   * X-Platform-Organisation-Id header, so it does NOT switch the operator's
   * selected organisation — their session keeps pointing wherever it was, and
   * an org switch can't re-target a sweep that is already running.
   */
  const handleOpenSweep = useCallback((org: Organisation) => {
    setSweepOrg(org);
  }, []);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organisation | null>(null);
  const [deletingOrg, setDeletingOrg] = useState<Organisation | null>(null);

  // Form state
  const { options: countryOptions, countries: countryRows } = useCountries();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    slug: "",
    time_zone: "UTC",
    country_code: "ZA",
    billing_currency: "",
  });
  // The org's own VAT-treatment override, if it has one. NULL means it inherits
  // the country's — so the hint must resolve override-then-country, exactly as
  // the control plane does, or it would misreport a deliberately overridden org.
  const [editingOrgTreatment, setEditingOrgTreatment] = useState<
    CountryItem["vat_treatment"] | null
  >(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  // Whether the timezone is a deliberate choice rather than the "UTC" default.
  // Only an undeliberate one is replaced when the country changes. A ref, not
  // state: nothing renders from it, and the async auto-fill tail must read the
  // CURRENT value rather than the one captured when its request started.
  const timezoneChosenRef = useRef(false);
  // Monotonic ticket invalidating auto-fill replies that have been superseded.
  const autoFillRequestRef = useRef(0);

  // Server-side paginated data fetch
  const fetchOrganisations = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<Organisation>> => {
      if (!token) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      const response = await platformApi.listOrganisations(token, {
        offset: (page - 1) * pageSize,
        limit: pageSize,
        search: searchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      return {
        data: response.items || [],
        totalCount: response.total || 0,
        page,
        pageSize,
      };
    },
    [token, searchTerm, sortBy, sortOrder],
  );

  const {
    data: organisations,
    isLoading,
    error,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<Organisation>({
    fetchFn: fetchOrganisations,
    initialPageSize: 25,
    dependencies: [token, searchTerm, sortBy, sortOrder],
  });

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }
  function getSortDirection(column: string): "asc" | "desc" | null {
    return sortBy === column ? sortOrder : null;
  }

  // Generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Form handlers
  //
  // The auto-fill lookup is async, so three things can invalidate it while it is
  // in flight: the user picking a timezone, a second country change whose reply
  // arrives first, and the dialog being closed and reopened on another org.
  // `timezoneChosenRef` answers the first; `autoFillRequestRef` answers the
  // other two — every reset/open/country-change takes a new ticket, and a reply
  // holding a stale ticket is dropped. Refs rather than state because the async
  // tail must read the CURRENT value, not the one captured when it started.
  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      time_zone: "UTC",
      country_code: "ZA",
      // Blank on create: the control plane seeds it from the chosen country's
      // DefaultCurrency. Sending a guess here would be the client deciding
      // currency from country all over again.
      billing_currency: "",
    });
    setEditingOrgTreatment(null);
    timezoneChosenRef.current = false;
    autoFillRequestRef.current += 1;
  };

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const openEditDialog = (org: Organisation) => {
    setFormData({
      name: org.name,
      slug: org.slug,
      time_zone: org.time_zone || "UTC",
      country_code: org.country_code || "ZA",
      billing_currency: org.billing_currency || "",
    });
    setEditingOrgTreatment(org.vat_treatment ?? null);
    // An org that already carries a timezone has one on purpose — including a
    // deliberate UTC — so changing its country must never overwrite it.
    timezoneChosenRef.current = Boolean(org.time_zone);
    autoFillRequestRef.current += 1;
    setEditingOrg(org);
  };

  const handleTimezoneChange = (timezone: string) => {
    timezoneChosenRef.current = true;
    setFormData((previous) => ({ ...previous, time_zone: timezone }));
  };

  const handleCountryChange = async (countryCode: string) => {
    setFormData((previous) => ({ ...previous, country_code: countryCode }));
    if (timezoneChosenRef.current || !token) return;

    const ticket = (autoFillRequestRef.current += 1);
    const primary = await fetchPrimaryTimezone(token, countryCode);

    // Superseded by a later country change, a dialog reset, or a timezone the
    // user picked while this was in flight — their action wins, drop the reply.
    if (ticket !== autoFillRequestRef.current) return;
    if (timezoneChosenRef.current || !primary) return;

    setFormData((previous) => ({ ...previous, time_zone: primary }));
  };

  const closeDialogs = () => {
    setShowCreateDialog(false);
    setEditingOrg(null);
    resetForm();
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setFormSubmitting(true);
    try {
      if (editingOrg) {
        // `billing_currency` is sent EXPLICITLY, echoing the org's current
        // value. The control plane no longer derives currency from country on
        // PATCH, so omitting it would also be safe — but stating it means this
        // dialog can never be the thing that repriced a customer, whichever
        // control plane it is talking to. Omitted only when the org somehow
        // carries no currency, since the API rejects an empty string.
        await platformApi.updateOrganisation(token, editingOrg.id, {
          name: formData.name,
          time_zone: formData.time_zone,
          country_code: formData.country_code,
          ...(formData.billing_currency
            ? { billing_currency: formData.billing_currency }
            : {}),
        });
        toast.success("Organisation updated successfully");
      } else {
        // Create DOES pass the country — the picked value used to be dropped
        // silently, leaving every new org on the ZA default. The currency is
        // deliberately not sent: the server seeds it from the country row.
        await platformApi.createOrganisation(token, {
          name: formData.name,
          slug: formData.slug,
          time_zone: formData.time_zone,
          country_code: formData.country_code,
        });
        toast.success("Organisation created successfully");
      }
      closeDialogs();
      refresh();
      refreshOrganisations();
    } catch (err) {
      console.error("Failed to save organisation:", err);
      toast.error(
        editingOrg
          ? "Failed to update organisation"
          : "Failed to create organisation",
      );
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!token || !deletingOrg) return;

    setDeleteSubmitting(true);
    try {
      await platformApi.deleteOrganisation(token, deletingOrg.id);
      toast.success("Organisation deleted successfully");
      setDeletingOrg(null);
      refresh();
      refreshOrganisations();
    } catch (err) {
      console.error("Failed to delete organisation:", err);
      toast.error("Failed to delete organisation");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // Navigation handlers
  const handleViewUsers = (org: Organisation) => {
    navigate({
      to: "/portal/platform/users",
      search: { organisation_id: org.id },
    });
  };

  const handleViewSubscriptions = (org: Organisation) => {
    navigate({
      to: "/portal/platform/subscriptions",
      search: { organisation_id: org.id },
    });
  };

  /**
   * Blank-select guard.
   *
   * A Radix Select whose `value` matches no item renders EMPTY — which is how
   * Beach Hotel Group's real country (NA) showed as an empty Country field and
   * invited an admin to "fix" it with a wrong one. If the org's code is missing
   * from whatever list is in hand, prepend it as a literal so the field always
   * shows something true. The list is the server's ~249 countries, so this
   * fires only offline or on a code the seed does not carry.
   */
  const selectableCountries = useMemo(() => {
    const code = formData.country_code;
    if (!code || countryOptions.some((c) => c.value === code)) {
      return countryOptions;
    }
    return [{ value: code, label: code }, ...countryOptions];
  }, [countryOptions, formData.country_code]);

  /**
   * What the edit dialog reports as the VAT treatment: the org's own override
   * if it has one, else the country's. `resolveVatTreatment` is shared with the
   * other pages that word this, and mirrors CP's `resolve_vat_context`, so the
   * hint cannot disagree with the invoice.
   */
  const effectiveVat = useMemo(
    () =>
      resolveVatTreatment(
        countryRows,
        formData.country_code,
        editingOrgTreatment,
      ),
    [countryRows, formData.country_code, editingOrgTreatment],
  );

  const selectedCountryRow = countryRows.find(
    (c) => c.code === formData.country_code,
  );

  // A new organisation has no override yet, so it inherits its country's
  // treatment outright — passing `editingOrgTreatment` here would leak the
  // previously-edited org's override into the create dialog.
  const newOrgVat = useMemo(
    () => resolveVatTreatment(countryRows, formData.country_code, null),
    [countryRows, formData.country_code],
  );

  // Calculate stats from current page data
  const activeCount = organisations.filter((o) => o.is_active).length;
  const inactiveCount = organisations.filter((o) => !o.is_active).length;

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Organisations</h1>
          <p className="mt-2 text-lg text-slate-600">
            Manage all organisations on the platform
          </p>
        </div>
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="py-12 text-center">
            <p className="text-destructive">
              Failed to load organisations: {error}
            </p>
            <Button onClick={refresh} variant="outline" className="mt-4">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Organisations</h1>
          <p className="mt-2 text-lg text-slate-600">
            Manage all organisations on the platform
          </p>
        </div>
        <Button onClick={openCreateDialog} className="rounded-full">
          <Plus className="mr-2 h-4 w-4" />
          Create Organisation
        </Button>
      </div>

      {/* Content Panel */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search organisations..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Button variant="outline" size="sm" onClick={refresh}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Organisations
              </CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pagination.totalCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Active (this page)
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Inactive (this page)
              </CardTitle>
              <XCircle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{inactiveCount}</div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : organisations.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? "No organisations match your search" : "No organisations yet"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm ? "Try a different search term." : "Create your first organisation to get started."}
            </p>
            {!searchTerm && (
              <Button onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Create Organisation
              </Button>
            )}
          </div>
        ) : (
          <>
            <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead direction={getSortDirection("name")} onSort={() => handleSort("name")}>Organisation</SortableTableHead>
                    <SortableTableHead direction={getSortDirection("is_active")} onSort={() => handleSort("is_active")}>Status</SortableTableHead>
                    <SortableTableHead direction={getSortDirection("user_count")} onSort={() => handleSort("user_count")}>Users</SortableTableHead>
                    <SortableTableHead direction={getSortDirection("subscription_count")} onSort={() => handleSort("subscription_count")}>Subscriptions</SortableTableHead>
                    <SortableTableHead direction={getSortDirection("created_at")} onSort={() => handleSort("created_at")}>Created</SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[50px]">{" "}</SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organisations.map((org) => (
                    <TableRow key={org.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <div className="font-medium">{org.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {org.slug}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={org.is_active ? "success" : "destructive"}
                        >
                          {org.is_active ? (
                            <span className="flex items-center">
                              <CheckCircle className="h-3 w-3 mr-1" /> Active
                            </span>
                          ) : (
                            <span className="flex items-center">
                              <XCircle className="h-3 w-3 mr-1" /> Inactive
                            </span>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span>{org.user_count ?? "-"}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <CreditCard className="h-4 w-4 text-muted-foreground" />
                          <span>{org.subscription_count ?? "-"}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(org.created_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            {/*
                              Icon-only, so it needs an explicit name: without
                              one a screen reader announces every row's control
                              identically as "button", and tests can only reach
                              it positionally via its row.
                            */}
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Actions for ${org.name}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => openEditDialog(org)}
                            >
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit Organisation
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleViewUsers(org)}
                            >
                              <Users className="h-4 w-4 mr-2" />
                              View Users
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleViewSubscriptions(org)}
                            >
                              <CreditCard className="h-4 w-4 mr-2" />
                              View Subscriptions
                            </DropdownMenuItem>
                            {canSweep && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleOpenSweep(org)}
                                >
                                  <Wrench className="h-4 w-4 mr-2" />
                                  Maintenance Sweep
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeletingOrg(org)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Organisation
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

            {/* Pagination */}
            {pagination.totalCount > 0 && (
              <div className="mt-4 border-t pt-4">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  pageSizeOptions={pageSizeOptions}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  canGoPrev={canGoPrev}
                  canGoNext={canGoNext}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  onFirstPage={firstPage}
                  onLastPage={lastPage}
                  onPrevPage={prevPage}
                  onNextPage={nextPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog
        open={showCreateDialog || !!editingOrg}
        onOpenChange={(open) => !open && closeDialogs()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingOrg ? "Edit Organisation" : "Create Organisation"}
            </DialogTitle>
            <DialogDescription>
              {editingOrg
                ? "Update the organisation details below."
                : "Fill in the details to create a new organisation."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleFormSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Organisation Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => {
                    const newName = e.target.value;
                    setFormData({
                      ...formData,
                      name: newName,
                      slug: editingOrg ? formData.slug : generateSlug(newName),
                    });
                  }}
                  placeholder="Acme Corporation"
                  required
                />
              </div>

              {!editingOrg && (
                <div className="grid gap-2">
                  <Label htmlFor="slug">Slug (URL-friendly identifier)</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        slug: e.target.value.toLowerCase(),
                      })
                    }
                    placeholder="acme-corp"
                    pattern="[a-z0-9-]+"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Only lowercase letters, numbers, and hyphens
                  </p>
                </div>
              )}

              <div className="grid gap-2">
                <Label htmlFor="time_zone">Time Zone</Label>
                <TimezoneCombobox
                  token={token}
                  value={formData.time_zone}
                  country={formData.country_code}
                  onValueChange={handleTimezoneChange}
                />
              </div>

              <div className="grid gap-2">
                <Label>Country</Label>
                <Select
                  value={formData.country_code}
                  onValueChange={(value) => handleCountryChange(value)}
                >
                  <SelectTrigger data-testid="organisation-country-select">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectableCountries.map((country) => (
                      <SelectItem key={country.value} value={country.value}>
                        {country.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editingOrg ? (
                  <p className="text-xs text-muted-foreground">
                    Billing currency: {formData.billing_currency || "—"}
                    {vatTreatmentHint(effectiveVat.treatment, effectiveVat.rate)}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Billing currency:{" "}
                    {selectedCountryRow?.default_currency ?? "—"}
                    {vatTreatmentHint(newOrgVat.treatment, newOrgVat.rate)}
                  </p>
                )}
              </div>

              {editingOrg && (
                <div className="grid gap-2">
                  <Label htmlFor="billing_currency">Billing Currency</Label>
                  <Input
                    id="billing_currency"
                    value={formData.billing_currency || "—"}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    Currency is not derived from country. Changing it reprices
                    the organisation, so it is a separate deliberate action.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialogs}>
                Cancel
              </Button>
              <Button type="submit" disabled={formSubmitting}>
                {formSubmitting && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {editingOrg ? "Save Changes" : "Create Organisation"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletingOrg}
        onOpenChange={(open) => !open && setDeletingOrg(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Organisation?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingOrg?.name}"? This action
              cannot be undone. All associated data including users and
              subscriptions will be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSubmitting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Delete Organisation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Keyed on the client so switching clients starts a clean sweep rather
          than showing the previous client's results. */}
      {sweepOrg && (
        <ClientMaintenanceSweepDrawer
          key={sweepOrg.id}
          open={!!sweepOrg}
          onOpenChange={(open) => !open && setSweepOrg(null)}
          organisationName={sweepOrg.name}
          organisationId={sweepOrg.id}
          token={token}
        />
      )}
    </div>
  );
}
